import React, { useMemo, useEffect } from 'react';
import { Typography, Box, Link as MuiLink } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  getFormattedDateFromIsoString,
  getFormattedTimeFromIsoString,
} from '~/src/utils/date';
import { clioManageMatterLink } from '~/src/utils/clio';
import { useMatterDetail } from '~/src/hooks/useMatterDetail';
import { useAIFieldMapperEnabled } from '~/src/entities/user';
import useMst from '~/src/hooks/useMst';

export type MatterSyncData = {
  id: number;
  matterNumber?: number | null;
  title: string;
  externalMatterId?: string | null;
  lastSynced?: string | null;
};

type MatterInfoHeaderProps = {
  matterId?: number;
  clioMatterId?: string;
  fallbackMatterName: string;
};

export const MatterInfoHeader = ({
  matterId,
  clioMatterId,
  fallbackMatterName,
}: MatterInfoHeaderProps) => {
  const isAIFieldMapperEnabled = useAIFieldMapperEnabled();
  const { singleMatterSyncFailed: hasClioSyncError } = useMst((store) => ({
    singleMatterSyncFailed: store.clio.singleMatterSyncFailed,
  }));

  // Fetch matter data only if we have a matterId and clioMatterId (AI Field Mapper enabled)
  const shouldFetchMatter =
    isAIFieldMapperEnabled && !!matterId && !!clioMatterId;
  const { data: matterData, remove } = useMatterDetail(matterId ?? 0, {
    enabled: shouldFetchMatter,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Clear the query cache on unmount to prevent stale cached data from affecting
  // MatterDetailsPage when navigating back after the matter store has been overwritten
  // by the matters list fetch
  useEffect(() => remove, [remove]);

  const matterSyncData = useMemo(() => {
    if (!matterData) return undefined;
    return {
      id: matterData.id,
      matterNumber: matterData.matterNumber,
      title: matterData.title,
      externalMatterId: matterData.externalMatterId,
      lastSynced: matterData.lastSynced,
    };
  }, [matterData]);

  const matterTitle = matterSyncData?.matterNumber
    ? `${matterSyncData.matterNumber} - ${matterSyncData.title}`
    : fallbackMatterName;

  const lastSyncedText = matterSyncData?.lastSynced
    ? `Last refreshed: ${getFormattedDateFromIsoString(
        matterSyncData.lastSynced,
      )} at ${getFormattedTimeFromIsoString(matterSyncData.lastSynced)}`
    : null;

  // Only show enhanced matter info if AI Field Mapper is enabled AND we have the data
  const showEnhancedMatterInfo =
    isAIFieldMapperEnabled && matterId && matterSyncData && clioMatterId;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '8px',
        }}
      >
        <Typography variant="label">Matter</Typography>
      </Box>

      {showEnhancedMatterInfo ? (
        <Box
          sx={{
            marginBottom: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <MuiLink
            href={clioManageMatterLink(clioMatterId)}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline',
              color: 'primary.main',
              textDecoration: 'none',
              fontSize: '14px',
              lineHeight: '20px',
              fontWeight: 500,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {matterTitle}
          </MuiLink>
          <OpenInNewIcon
            sx={{
              fontSize: '14px',
              color: 'primary.main',
              flexShrink: 0,
            }}
          />
        </Box>
      ) : (
        <Typography
          sx={{
            fontSize: '14px',
            lineHeight: '20px',
            fontWeight: 500,
          }}
        >
          {fallbackMatterName}
        </Typography>
      )}

      {showEnhancedMatterInfo && lastSyncedText && (
        <Typography
          sx={{
            fontSize: '12px',
            lineHeight: '16px',
            fontWeight: 400,
            color: 'text.secondary',
            display: 'block',
          }}
        >
          {lastSyncedText}
        </Typography>
      )}

      {showEnhancedMatterInfo && hasClioSyncError && (
        <Typography
          sx={{
            fontSize: '12px',
            lineHeight: '16px',
            fontWeight: 400,
            color: '#B45309',
            display: 'block',
            marginTop: '4px',
          }}
        >
          We could not get the latest Clio Manage data. Report this issue to our
          support team.
        </Typography>
      )}
    </>
  );
};
